/**
 * Zustand, der einen Neustart übersteht.
 *
 * Für Ansichts- und Reiterwahlen: Wer die Timeline eingestellt hat, will nach
 * F5 nicht wieder im Monatsraster landen.
 *
 * **Bewusst nicht gespeichert wird die Stelle in der Zeit** — die angezeigte
 * Woche und der gewählte Timeline-Tag. Das sind keine Einstellungen, sondern
 * eine Position: Gespeichert stünde man morgen noch in der Woche von gestern.
 * Sie setzen sich absichtlich auf „jetzt" zurück.
 */

import { useCallback, useState } from 'react'

/** Gemeinsames Präfix, damit die Schlüssel im Speicher zuzuordnen sind. */
const PREFIX = 'dienstplan:'

/**
 * Liest einen gespeicherten Wert und prüft ihn.
 *
 * ⚠️ Die Prüfung ist keine Förmelei. Im Speicher steht, was eine **frühere**
 * Fassung der Anwendung dort abgelegt hat. Wird eine Ansicht umbenannt oder
 * entfernt, bekäme die neue Fassung sonst einen Namen zurück, den sie nicht
 * kennt — und rendert eine leere Seite, die sich auch durch Neuladen nicht
 * heilt, weil der kaputte Wert ja gespeichert bleibt.
 */
export function readPersisted<T>(key: string, istGueltig: (wert: unknown) => wert is T): T | null {
  try {
    const roh = window.localStorage.getItem(PREFIX + key)
    if (roh === null) return null
    const wert: unknown = JSON.parse(roh)
    return istGueltig(wert) ? wert : null
  } catch {
    // Kaputtes JSON, abgeschalteter Speicher, privater Modus — in allen
    // Fällen ist der Standardwert die richtige Antwort, keine Ausnahme.
    return null
  }
}

export function writePersisted(key: string, wert: unknown): void {
  try {
    window.localStorage.setItem(PREFIX + key, JSON.stringify(wert))
  } catch {
    // Safari im privaten Modus wirft beim Schreiben. Eine gemerkte Vorliebe
    // ist Komfort — sie darf die Bedienung nicht anhalten.
  }
}

/**
 * Wie `useState`, nur dass der Wert einen Neustart übersteht.
 *
 * `istGueltig` entscheidet, ob ein gespeicherter Wert noch brauchbar ist.
 * Alles andere fällt auf `fallback` zurück.
 */
export function usePersistentState<T>(
  key: string,
  fallback: T,
  istGueltig: (wert: unknown) => wert is T,
): [T, (wert: T) => void] {
  const [wert, setWert] = useState<T>(() => readPersisted(key, istGueltig) ?? fallback)

  const setzen = useCallback(
    (neu: T) => {
      setWert(neu)
      writePersisted(key, neu)
    },
    [key],
  )

  return [wert, setzen]
}

/** Baut eine Prüfung aus einer festen Liste erlaubter Werte. */
export function oneOf<const T extends readonly string[]>(
  erlaubt: T,
): (wert: unknown) => wert is T[number] {
  return (wert): wert is T[number] => typeof wert === 'string' && erlaubt.includes(wert)
}
